'use client';
import { Box, TextField } from '@mui/material';
import { useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { AbgTypography, Alert, AlertType, useTranslation } from '@abg/kit-base';
import { plainDtos } from '@abg/shared';
import { EMAIL_REGEX_FORWARD_MAIL } from '@abg/shared/regex';
import { useBffClient } from '@abg/web-core/bff/data/client-provider';

import { getLastNameFromSearchParams } from '../../shared/reservation';
import { bookingFlowStoreSelector } from '../../store';
import { ActionDialog } from '../ActionDialog';

import { containerStyle, fieldLabel, inputField } from './forward-mail.styling';
import { ForwardMailProps } from './types'; // Import types
import { getForwardMailPayload } from './utils';

// Validate email format using a regex
const validateEmail = (email: string): boolean => {
  return EMAIL_REGEX_FORWARD_MAIL.test(email);
};

export default function ForwardMail({
  styleParts = {},
  isOpen,
  onClose,
  i18n,
  reservationDetails,
}: ForwardMailProps) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(isOpen);
  const [email, setEmail] = useState<string>('');
  const [retypeEmail, setRetypeEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [retypeEmailError, setRetypeEmailError] = useState<string>('');
  const [messageContainer, setMessageContainer] = useState<Element | null>(
    null,
  );
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState(AlertType.CONFIRMATION);
  const [alertDescription, setAlertDescription] = useState<string>('');
  const [mailPayload, setMailPayload] =
    useState<plainDtos.SendReservationEmailRequestDto | null>(null);
  const getState = bookingFlowStoreSelector.use.getState();
  const customerDataFromCheckoutStore = getState();
  const confirmationNumberfromConfirmation =
    bookingFlowStoreSelector.use.confirmationNumberfromConfirmation();
  const bffClient = useBffClient();
  const t = useTranslation(i18n);
  const searchParams = useSearchParams();

  // Set the container for rendering the alert message
  useEffect(() => {
    if (customerDataFromCheckoutStore.checkoutfirstName) {
      const mailPayloadData: plainDtos.SendReservationEmailRequestDto =
        getForwardMailPayload(customerDataFromCheckoutStore);
      setMailPayload(mailPayloadData);
      return;
    }

    // Extract lastName from URL params when available, reservation details page
    const lastNameFromUrl = getLastNameFromSearchParams(searchParams) ?? '';

    if (reservationDetails && reservationDetails.confirmationNumber) {
      const payloadLastName =
        lastNameFromUrl ||
        reservationDetails?.customerInformation?.lastName ||
        '';

      const mailPayloadData: plainDtos.SendReservationEmailRequestDto = {
        confirmationNumber: reservationDetails.confirmationNumber,
        firstName: reservationDetails?.customerInformation?.firstName || '',
        lastName: payloadLastName,
        email: '',
      };

      setMailPayload(mailPayloadData);
      return;
    }
  }, [
    customerDataFromCheckoutStore,
    confirmationNumberfromConfirmation,
    reservationDetails,
    searchParams,
  ]);

  useEffect(() => {
    const containerElement = document.querySelector(
      '#confirmation-message-container',
    );
    if (containerElement) {
      setMessageContainer(containerElement);
    }
  }, []);

  useEffect(() => {
    setDialogOpen(isOpen);
  }, [isOpen]);

  // Handle form submission and perform validations
  const handleSubmit = (): void => {
    const isEmailValid = validateEmail(email);
    const isRetypeEmailValid = validateEmail(retypeEmail);
    setShowAlert(true);

    if (isEmailValid) {
      setEmailError('');
    } else {
      setEmailError(
        t('reservationConfirmation.forwardMail.popup.email.errorMessage'),
      );
    }

    if (isRetypeEmailValid) {
      setRetypeEmailError('');
    } else {
      setRetypeEmailError(
        t('reservationConfirmation.forwardMail.popup.email.errorMessage'),
      );
    }

    if (isEmailValid && isRetypeEmailValid) {
      if (email === retypeEmail) {
        sendEmail(email).catch(() => {});
      } else {
        setRetypeEmailError(
          t(
            'reservationConfirmation.forwardMail.popup.retypeEmail.errorMessage',
          ),
        );
      }
    }
  };

  // Reset form state and close the dialog
  const handleDialogClose = useCallback(
    ({ scrollToTop }: { scrollToTop: boolean }): void => {
      setEmail('');
      setRetypeEmail('');
      setEmailError('');
      setRetypeEmailError('');
      setDialogOpen(false);
      if (scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the page
      }
      onClose();
    },
    [],
  );

  // Close the alert message
  const handleAlertClose = useCallback((): void => {
    setShowAlert(false);
  }, []);

  // Validate retype email on blur
  const handleRetypeEmailBlur = useCallback((): void => {
    // First check if the email format is valid
    if (!validateEmail(retypeEmail)) {
      setRetypeEmailError(
        t('reservationConfirmation.forwardMail.popup.email.errorMessage'),
      );
      return;
    }

    // If format is valid, check if emails match
    if (email !== retypeEmail) {
      setRetypeEmailError(
        t('reservationConfirmation.forwardMail.popup.retypeEmail.errorMessage'),
      );
      return;
    }

    // Clear error if everything is valid
    setRetypeEmailError('');
  }, [email, retypeEmail, t]);

  const sendEmail = async (email: string): Promise<void> => {
    try {
      const response =
        await bffClient.reservation<plainDtos.ReservationEmailResponseDto>(
          `/booking/email`,
          {
            body: JSON.stringify({ ...mailPayload, email }),
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
      if (response) {
        setAlertType(AlertType.CONFIRMATION);
        setAlertDescription(
          t('reservationConfirmation.forwardMail.success.message', {
            email: email,
          }),
        );
        handleDialogClose({ scrollToTop: true }); // Close the dialog after success
        setTimeout(() => {
          setShowAlert(true);
        }, 1000);
      } else {
        handleDialogClose({ scrollToTop: true }); // Close the dialog after response
        setAlertType(AlertType.ERROR);
        setAlertDescription('Failed to send email.');
        setShowAlert(true);
      }
    } catch (error) {
      handleDialogClose({ scrollToTop: true }); // Close the dialog after response
      setAlertType(AlertType.ERROR);
      setAlertDescription(
        typeof error === 'object' && error !== null && 'message' in error
          ? (error as { message: string }).message
          : '',
      );
      setShowAlert(true);
    }
  };

  return (
    <>
      {/* Render the alert message in the specified container */}
      {messageContainer &&
        createPortal(
          <Alert
            title=""
            open={showAlert}
            level={2}
            type={alertType}
            onClose={handleAlertClose}
            description={alertDescription}
          />,
          messageContainer,
        )}
      <ActionDialog
        isOpen={dialogOpen}
        onClose={() => handleDialogClose({ scrollToTop: false })}
        title={t('reservationConfirmation.forwardMail.popup.title')}
        description={t('reservationConfirmation.forwardMail.popup.description')}
        onPrimaryAction={handleSubmit}
        primaryActionLabel={t(
          'reservationConfirmation.forwardMail.popup.button.label',
        )}
        styleParts={styleParts}
        primaryActionDisabled={false}
      >
        <form>
          <Box>
            {/* Email input field */}
            <AbgTypography sx={fieldLabel} variant="body2">
              <label htmlFor="email">
                {t('reservationConfirmation.forwardMail.popup.email.label')}
              </label>
            </AbgTypography>
            <TextField
              id="email"
              sx={inputField}
              type="email"
              value={email}
              onChange={(event_) => setEmail(event_.target.value)}
              placeholder={t(
                'reservationConfirmation.forwardMail.popup.email.placeholder',
              )}
              onBlur={() =>
                setEmailError(
                  validateEmail(email)
                    ? ''
                    : t(
                        'reservationConfirmation.forwardMail.popup.email.errorMessage',
                      ),
                )
              }
              error={!!emailError}
              helperText={
                emailError ? (
                  <span dangerouslySetInnerHTML={{ __html: emailError }}></span>
                ) : null
              }
              required
              fullWidth
            />
          </Box>
          <Box sx={containerStyle}>
            {/* Retype email input field */}
            <AbgTypography sx={fieldLabel} variant="body2">
              <label htmlFor="retypeEmail">
                {t(
                  'reservationConfirmation.forwardMail.popup.retypeEmail.label',
                )}
              </label>
            </AbgTypography>
            <TextField
              id="retypeEmail"
              type="email"
              value={retypeEmail}
              sx={inputField}
              onChange={(event_) => setRetypeEmail(event_.target.value)}
              placeholder={t(
                'reservationConfirmation.forwardMail.popup.retypeEmail.placeholder',
              )}
              onBlur={handleRetypeEmailBlur}
              error={!!retypeEmailError}
              helperText={
                retypeEmailError ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: retypeEmailError }}
                  ></span>
                ) : null
              }
              required
              fullWidth
            />
          </Box>
        </form>
      </ActionDialog>
    </>
  );
}
